"use client";

// B3.4 CART TRAY — the real, store-driven cart panel. HIDDEN by default (renders nothing to the static
// page flow, so the pixel gate never sees it); shown only on interaction. It drives the founder rule
// end-to-end: hold items signed-out, sign in, watch the DRAFT ORDER appear, cart survives.
//
// The COLLAPSED cart badge is NOT here — it is the captured mirror markup in the header toolbar
// (button[aria-label="Open cart"]); MarketplaceCartLayer wires that button's click + keeps its
// MuiBadge count in sync with this store, so the mirror chrome stays byte-identical (pixel gate intact).
//
// ---------------------------------------------------------------------------------------------------
// WO-GF-001-P1B-S18 · F123 — REBUILT ON LIVE'S REAL AXIS. THIS CORRECTS WHAT F111 SHIPPED.
//
// F111 grouped the tray by RENTALS / ACTIVITIES / OTHER. That is the My Tickets SUB-tab axis and it was
// ONE LEVEL TOO DEEP. The founder's live cart screenshot (banked as LIVE-CART-AND-QUICKVIEW-REFERENCE-
// 2026-07-23.md) splits the cart by **MARKETPLACE vs TRANSPORT** — the My Tickets CATEGORY-tab axis. A
// live cart holding a zipline and two bus seats reads as 1 marketplace line + 2 transport lines; the old
// axis could not express a transport line at all and would have filed the zipline under ACTIVITIES.
//
// That axis is also what makes the billing block work: `Transport $41` / `Marketplace $94` ARE the
// per-section subtotals, so the sections and the billing rows are the same partition read twice.
//
// Live's anatomy, top to bottom, reproduced here in order:
//   1. header    — `Total $x` bold, `Snowclub savings $y` orange beneath, `×` right
//   2. MARKETPLACE section — store icon, grey caps, `Edit` (orange, underlined) + collapse chevron
//        · line: product title (bold, wraps) over supplier name (grey); right-aligned `1 X $94`
//   3. TRANSPORT  section — bus icon, same `Edit` + chevron
//   4. PROMO CODE input + `Apply`
//   5. billing rows — `Transport` · `Marketplace` · `Promo Code −$0.00` · `Taxes & Fees`
//   6. Driver Tip — `$` + small editable input, noted as applying to both departure and return
//   7. `CONTINUE` — orange, centred
//   8. footer — `Total` + `Snowclub savings` repeated
//
// CART LOGIC IS UNTOUCHED. The sections are a render-time partition of the same `items` array; every
// line still renders exactly once, every data-cart-* hook is unchanged, and the store's sign-in,
// reconcile() and checkout handoff are not touched at all. The charged basis stays `guestPrice`, so the
// crown-jewel totals do not move.
//
// DECLARED DELTAS (recorded, not silent):
//   • F85/TAXES — "Taxes & Fees" still has NO source in this build (no ported artifact, no BE endpoint
//     computes it before checkout). Live shows $13.33; printing a number here would be inventing money
//     data (the F81 rule). The row renders live's structure and asserts no figure, so `Total` is
//     lines + tip and visibly EXCLUDES tax. Founder to rule.
//   • F80 STANDS — the promo input is `disabled` and `Apply` is a decorative span. The reference doc
//     doubted this off a screenshot; S17 re-verified it on live with a genuinely NON-EMPTY cart
//     (input.disabled true, container `notAllowed`, "Apply" a <span>). A screenshot cannot show
//     `disabled`. Closed — do not reopen.
//   • SECTION ICONS — live's own captured `marketplace` / `transport` nav art is reused here rather than
//     a hand-drawn glyph. It is real live art for exactly these two concepts; whether live's CART icons
//     are byte-identical to its NAV icons is not observable from a screenshot, so this is recorded.
//   • `Edit` — a screenshot shows a LABEL, not a handler (the F119 rule, and the F127/F128 lesson that an
//     anchor existing is not evidence a control navigates to it). Rather than render it inert, each Edit
//     resolves through the EXISTING nav chokepoints (`hudNavHref` / `mountainHubHref`) off the line's own
//     `mountain` — so it lands on an already-served page and cannot dangle. The destination is an
//     inference from the label; flagged here for the founder's walk.
//   • empty sections and their subtotal rows are not rendered (the F111 precedent — the cart never grows
//     a heading, or a billing row, for something it does not hold).
//   • the Driver Tip renders only when a TRANSPORT line exists, because live's own note ties the tip to
//     the departure and return legs. A tip with no bus booked would be a control with nothing to tip.
//   • the in-tray SIGN-IN block has no counterpart on live (live's tray is guest-only). It is
//     load-bearing for the founder-migration-run gate, so it stays — a port-side affordance, recorded.
// ---------------------------------------------------------------------------------------------------

import { useState } from "react";
import type { CartItem, CartSection } from "@/lib/cart-store";
import { isSignedIn, useCartStore } from "@/lib/cart-store";
import { gaItemFromCartItem, trackRemoveFromCart } from "@/lib/analytics-event";
import { cartReviewHref } from "@/lib/cart-entry";
import { HUB_NAV_ITEMS } from "@/components/site/hubNavIcons";
import {
  cartMountains,
  funnelCheckoutHref,
  isMixedMountain,
  lineEditHref,
  lineSaving,
  lineTotal,
  linePrice,
  money,
  mountainLabelOf,
  trayCheckoutHref,
} from "@/lib/cart-billing";

// Live's own captured hub-nav art, reused for the two cart section headings. The capture's SVGs carry
// generated `id=`/`url(#…)` pairs; the hub header renders the same art on the same page, so the ids are
// suffixed here to keep them unique rather than emitting duplicates into the document.
const iconFor = (label: string): string => {
  const raw = HUB_NAV_ITEMS.find((i) => i.label === label)?.svg ?? "";
  return raw.replace(/(id="|url\(#)([A-Za-z0-9_]+)/g, (_m, p1, p2) => `${p1}${p2}_cart`);
};

const SECTIONS: { key: CartSection; label: string; icon: string }[] = [
  { key: "marketplace", label: "MARKETPLACE", icon: iconFor("marketplace") },
  { key: "transport", label: "TRANSPORT", icon: iconFor("transport") },
];


// S27 · F117 — the billing arithmetic and the Edit destination rule now live in ONE module shared with
// the full cart PAGE (`@/lib/cart-billing`). Same functions, same behaviour; extracted so the flyout and
// the page cannot print different money for the same basket.

/**
 * S25 · W20/R5 — THE CONTRACTUAL CART CTA WHILE SELECTING TRANSPORT.
 *
 * On the results screens (`/booking/schedule/{depart|return}/…`) the tray is the funnel-forward
 * mechanism, exactly as live's is (BOOKING-FLOW-CONTRACT §5). Its CTA is contextual:
 *   • roundtrip, on the RETURN leg, with no return line chosen yet → "Select Your Return Bus Time"
 *     (a prompt, not a nav — clicking it just closes the tray so the traveller picks the return);
 *   • one-way (sitting on the depart screen — roundtrip-depart auto-advanced away), OR the return
 *     leg once the return IS chosen → "Continue", which advances to `/mountain-{code}/booking/details`.
 *
 * Read from the URL + the cart's own lines; returns null off the results screens so every other cart
 * (marketplace, the review-page hold) keeps its existing sign-in / checkout block untouched. Guest-safe:
 * the funnel is guest-navigable to DETAILS, so this CTA never gates on a session.
 */
type FunnelForward = { mode: "continue"; href: string } | { mode: "return-prompt" } | null;
function funnelForward(items: CartItem[]): FunnelForward {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/mountain-([^/]+)\/booking\/schedule\/(depart|return)\//);
  if (!m) return null;
  const [, mountain, leg] = m;
  const hasTransport = items.some((i) => (i.section ?? "marketplace") === "transport");
  if (!hasTransport) return null; // nothing selected yet → no forward CTA (live shows none)
  const hasReturn = items.some((i) => i.title === "BUS SEAT (Return)");
  if (leg === "return" && !hasReturn) return { mode: "return-prompt" };
  return { mode: "continue", href: `/mountain-${mountain}/booking/details` };
}

export default function CartTray() {
  const items = useCartStore((s) => s.items);
  const open = useCartStore((s) => s.open);
  const session = useCartStore((s) => s.session);
  const reconciling = useCartStore((s) => s.reconciling);
  const lastError = useCartStore((s) => s.lastError);
  const driverTip = useCartStore((s) => s.driverTip);
  const setOpen = useCartStore((s) => s.setOpen);
  const setDriverTip = useCartStore((s) => s.setDriverTip);
  const removeItem = useCartStore((s) => s.removeItem);
  const signIn = useCartStore((s) => s.signIn);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (!open) return null; // hidden by default → the static gate never sees the tray

  const signedIn = isSignedIn(session);
  const booked = items.filter((i) => i.booked && i.orderDocumentId);
  const firstOrder = booked[0]?.orderDocumentId || null;

  // THE partition — one pass, read twice: once for the sections, once for the billing subtotals.
  const sections = SECTIONS.map((s) => {
    const lines = items.filter((i) => (i.section ?? "marketplace") === s.key);
    return { ...s, lines, subtotal: lines.reduce((sum, i) => sum + lineTotal(i), 0) };
  });
  const filled = sections.filter((s) => s.lines.length > 0);
  const hasTransport = sections.find((s) => s.key === "transport")!.lines.length > 0;
  // S30 · F175 — decided over the WHOLE basket, not per section, so a Whistler rental and a Big White
  // bus produce mountain headings in both sections rather than only in the one that happens to hold two.
  const mixedMountain = isMixedMountain(items);

  const itemsTotal = items.reduce((sum, i) => sum + lineTotal(i), 0);
  const savings = items.reduce((sum, i) => sum + lineSaving(i), 0);
  const total = itemsTotal + driverTip; // excludes tax — see the F85 delta in the header note

  // S25 · W20/R5 — on the transport results screens, the tray's bottom CTA is the funnel-forward
  // control (Continue → details / "Select Your Return Bus Time"), not the sign-in/checkout block.
  const fwd = funnelForward(items);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const r = await signIn(email, password);
    setBusy(false);
    if (!r.ok) setErr(r.error || "Sign in failed");
  };

  // SNOWCLUB-V101-STAGING-A1-ACCEPTANCE-001 — a basket with transport goes to the design review for the
  // bus's own mountain (the real server-priced Stripe rail), NOT the old F129 book-transport hold,
  // which wrote an unfunded pending_acceptance order and told the customer their seats were held.
  // Product-only baskets keep the draft-order hand-off (S35 · F211, `/?order=<id>`). One shared rule.
  const checkoutTarget = trayCheckoutHref(items);

  const checkout = () => {
    if (checkoutTarget) window.location.href = checkoutTarget;
  };

  const totalsBlock = (place: "top" | "bottom") => (
    <div style={place === "top" ? totalBar : bottomTotal}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 14 }}>Total</span>
          <strong {...(place === "top" ? { "data-cart-total": true } : {})} style={{ fontSize: 15 }}>
            {money(total)}
          </strong>
        </div>
        {savings > 0 ? (
          <span data-cart-savings={savings} style={savingsText}>
            Snowclub savings {money(savings)}
          </span>
        ) : null}
      </div>
      {place === "top" ? (
        <>
          <span style={vDivider} />
          <button type="button" aria-label="Close cart" onClick={() => setOpen(false)} style={closeBtn}>
            ×
          </button>
        </>
      ) : null}
    </div>
  );

  return (
    <div data-cart-tray role="dialog" aria-label="Cart" style={backdrop} onClick={() => setOpen(false)}>
      <aside style={panel} onClick={(e) => e.stopPropagation()}>
        {totalsBlock("top")}

        {items.length === 0 ? (
          <p data-cart-empty style={{ padding: "24px 16px", color: "#666", fontSize: 13, margin: 0 }}>
            Your cart is empty.
          </p>
        ) : (
          <ul style={list}>
            {filled.map(({ key, label, icon, lines, subtotal }) => {
              const isCollapsed = !!collapsed[key];
              // One line renderer, used by both the flat and the mountain-grouped paths, so the two can
              // never drift into rendering a line differently (F175).
              const renderLine = (i: CartItem) => (
                <li
                  key={i.id}
                  data-cart-line
                  data-line-id={i.id}
                  data-line-section={key}
                  data-line-category={i.category ?? "other"}
                  data-booked={i.booked ? "true" : "false"}
                  style={line}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{i.title}</div>
                    {/* live puts the SUPPLIER name in grey directly beneath the product title */}
                    <div data-line-supplier style={{ fontSize: 12, color: "#828282" }}>
                      {i.supplier}
                    </div>
                    {/* S30 · F175 — WHICH MOUNTAIN THIS LINE IS FOR, on EVERY line. The founder's mixed
                        basket rendered a Whistler rental and a Big White tour as adjacent rows of one
                        MARKETPLACE block with nothing distinguishing them; they only looked different
                        because those two product TITLES happen to name their resort, which is luck, not
                        attribution. Stated per line and not only in the group heading, because a line is
                        also read on its own (a collapsed section, a one-line section, the order summary). */}
                    {i.mountain ? (
                      <div
                        data-line-mountain={i.mountain}
                        style={{ fontSize: 11, color: "#1f2933", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}
                      >
                        <span aria-hidden style={{ color: ORANGE }}>▲</span>
                        {mountainLabelOf(i.mountain)}
                      </div>
                    ) : null}
                    {/* S30 · F176 — the date is LABELLED as the date the line is FOR. The value was
                        already rendered; what was missing is that it says what it is, so a bare date
                        could not be read as a booking date. A line with no date says so rather than
                        printing an em dash that reads like an empty range. */}
                    <div data-line-dates style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                      {i.startDate ? (
                        <>
                          For {i.startDate}
                          {i.endDate ? ` → ${i.endDate}` : ""}
                        </>
                      ) : (
                        "No date selected"
                      )}
                    </div>
                    {/* kept presentational-free (S12 · RW2-7) so nothing machine-facing regresses */}
                    {i.booked && i.orderDocumentId ? <span data-line-order={i.orderDocumentId} hidden /> : null}
                  </div>
                  <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* live's quantity notation: `1 X $94` */}
                    <div data-line-qty-price>
                      {i.qty} X {money(linePrice(i))}
                    </div>
                    {/* the two line actions sit on one row with a real separator — without the gap they
                        rendered as "EditRemove" (caught in the browser walk, not by any assertion). */}
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 2 }}>
                    {/* S30 · F178 — per-LINE Edit, back to THIS line's own surface with its selection.
                        The section-level Edit in the heading stays (it answers "change this whole
                        section"), but it lands on a hub and could never pre-select anything. */}
                    {lineEditHref(i) ? (
                      <a data-cart-line-edit href={lineEditHref(i)!} style={editLink}>
                        Edit
                      </a>
                    ) : null}
                      <button type="button" data-cart-remove onClick={() => { trackRemoveFromCart([gaItemFromCartItem(i)]); removeItem(i.id); }} style={linkBtn}>
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              );
              return (
                <li key={key} data-cart-section={key} data-cart-section-subtotal={subtotal}>
                  <div data-cart-section-heading style={sectionHeading}>
                    <span
                      aria-hidden
                      style={sectionIcon}
                      dangerouslySetInnerHTML={{ __html: icon }}
                    />
                    <span style={{ flex: 1 }}>{label}</span>
                    {/* S35 · F212 — section-level `Edit` removed here for the same reason as on the
                        full page; the two surfaces must not disagree about what Edit means. The per-LINE
                        Edit below is the real one (F178). */}
                    <button
                      type="button"
                      data-cart-section-toggle
                      aria-expanded={!isCollapsed}
                      aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${label}`}
                      onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
                      style={chevronBtn}
                    >
                      <span style={{ display: "inline-block", transform: isCollapsed ? "rotate(-90deg)" : "none" }}>
                        ⌄
                      </span>
                    </button>
                  </div>
                  {isCollapsed ? null : (
                    <ul style={list}>
                      {/* S30 · F175 — GROUPING. Within a section, lines are emitted mountain-by-mountain
                          and a heading is drawn ONLY when the basket actually mixes mountains. A
                          single-mountain basket (the overwhelmingly common one) renders byte-identically
                          to before, so no pixel baseline moves and the founder's common path is
                          untouched. The order of the groups and the rule itself live in cart-billing's
                          `cartMountains`, shared with the page, so the two surfaces cannot disagree. */}
                      {mixedMountain
                        ? cartMountains(lines).map((g) => (
                            <li key={g.mountain} data-cart-mountain-group={g.mountain} style={{ listStyle: "none" }}>
                              <div data-cart-mountain-heading={g.mountain} style={mountainHeading}>
                                <span aria-hidden style={{ color: ORANGE }}>▲</span>
                                <span style={{ flex: 1 }}>{g.label}</span>
                                <span style={{ color: "#828282", fontWeight: 500 }}>{money(g.subtotal)}</span>
                              </div>
                              <ul style={list}>{g.lines.map(renderLine)}</ul>
                            </li>
                          ))
                        : lines.map(renderLine)}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* F80 (S17-reconfirmed on live with a non-empty cart): the input is served `disabled` inside
            `notAllowed` and "Apply" is a decorative <span>, not a link. Reproduced exactly. */}
        <div data-cart-promo className="notAllowed" style={promoRow}>
          <input disabled placeholder="PROMO CODE" type="text" className="notAllowed form-control" style={promoInput} />
          <span style={{ fontSize: 13, color: "#2a9d8f" }}>Apply</span>
        </div>

        {/* live's billing block, in live's own row order (Transport, Marketplace, Promo, Taxes). The two
            subtotals ARE the section partition — same numbers, read a second time. */}
        <ul style={billingList}>
          {[...filled].reverse().map(({ key, label, subtotal }) => (
            <li key={key} style={billingRow} data-cart-billing={key}>
              {label.charAt(0) + label.slice(1).toLowerCase()}
              <span>{money(subtotal)}</span>
            </li>
          ))}
          <li style={billingRow}>
            <span style={{ color: "#f15a22" }}>Promo Code</span>
            <span>- $0</span>
          </li>
          <li style={billingRow} data-cart-taxes="unsourced" title="Taxes & fees are computed at checkout (F85)">
            Taxes &amp; Fees<span>—</span>
          </li>
        </ul>

        {/* live's Driver Tip: a `$` and a small editable input, with live's own note that the tip covers
            both the departure and the return leg. Rendered only when there IS a transport line. */}
        {hasTransport ? (
          <div data-cart-tip style={tipBox}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#222" }}>
              <span style={{ fontWeight: 600, flex: 1 }}>Driver Tip</span>
              <span style={{ color: "#828282" }}>$</span>
              <input
                data-cart-tip-input
                type="number"
                min={0}
                step="0.5"
                value={driverTip || ""}
                onChange={(e) => setDriverTip(parseFloat(e.target.value))}
                style={tipInput}
              />
            </label>
            <p style={{ margin: "6px 0 0", fontSize: 11, color: "#999" }}>
              Applies to both your departure and return.
            </p>
          </div>
        ) : null}

        {/* S35 · F211 — EMPTY-CART GATING. Everything below is the forward path, and an empty cart has
            nowhere to go forward to. Previously an empty tray still rendered a sign-in block, and for a
            signed-in shopper a `disabled` CONTINUE — a control that is present, greyed and does nothing.
            That is precisely the founder's "Continue dead": not a broken handler, a control rendered
            over an empty basket. An empty cart now renders its honest empty line and NO forward control
            at all (the S29 dead-control rule: if it cannot act, it must not be drawn). The sign-in block
            is unaffected on any basket that holds something, so the founder-migration run — which seeds
            items before signing in — is untouched. */}
        {items.length === 0 ? null : fwd ? (
          // W20/R5 — the funnel-forward CTA. Guest-safe (no session gate); the funnel is guest-navigable
          // to DETAILS, and the hold/sign-in happens later in the flow, not on the selection screens.
          <div data-cart-funnel-forward={fwd.mode} style={signinBox}>
            {fwd.mode === "return-prompt" ? (
              <button
                type="button"
                data-cart-return-prompt
                onClick={() => setOpen(false)}
                style={primaryBtn}
              >
                Select Your Return Bus Time
              </button>
            ) : (
              <a
                data-cart-continue
                href={fwd.href}
                style={{ ...primaryBtn, display: "block", textAlign: "center", textDecoration: "none" }}
              >
                Continue
              </a>
            )}
          </div>
        ) : !signedIn ? (
          <form data-cart-signin-form onSubmit={submit} style={signinBox}>
            {/* WO-FOUNDER-WALK-UNBLOCK-001 · T1 — CHECKOUT FIRST, SIGN-IN SECOND.
                The founder totalled a cart here and found no button: this block rendered a sign-in
                form and nothing else, so the only way forward was through a door phase 1 keeps
                locked (§14 RULING 1 refuses the snowclub-be booking write that mints the order id
                the old control needed). The checkout he was looking for is the funnel's REVIEW
                screen — his own export pay flow, fed from this cart — and nothing linked to it.
                Sign-in stays, below, where it belongs: it buys MEMBER FARES, it is not the gate on
                being able to check out at all. */}
            {funnelCheckoutHref(items) ? (
              <a
                data-cart-checkout-route="funnel"
                href={funnelCheckoutHref(items)!}
                style={{ ...primaryBtn, display: "block", textAlign: "center", textDecoration: "none", marginBottom: 10 }}
              >
                Proceed to checkout
              </a>
            ) : null}
            <div style={{ fontSize: 13, color: "#444", marginBottom: 8 }}>
              Sign in for member fares before you pay.
            </div>
            <input
              data-cart-email
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={input}
              autoComplete="username"
            />
            <input
              data-cart-password
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={input}
              autoComplete="current-password"
            />
            <button type="submit" data-cart-signin disabled={busy} style={primaryBtn}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            {(err || lastError) && (
              <div data-cart-error style={{ color: "#b42318", fontSize: 12, marginTop: 6 }}>
                {err || lastError}
              </div>
            )}
          </form>
        ) : (
          <div style={signinBox}>
            <div data-cart-signed-in style={{ fontSize: 13, color: "#1a7f37", marginBottom: 8 }}>
              Signed in as {session.email}
              {reconciling ? " · creating your order…" : booked.length > 0 ? ` · ${booked.length} in draft order` : ""}
            </div>
            <button
              type="button"
              data-cart-checkout
              data-order={firstOrder ?? ""}
              data-checkout-href={checkoutTarget ?? ""}
              onClick={checkout}
              disabled={!checkoutTarget}
              className={checkoutTarget ? undefined : "notAllowed"}
              style={{
                ...primaryBtn,
                opacity: checkoutTarget ? 1 : 0.55,
              }}
            >
              Continue
            </button>
          </div>
        )}

        {/* S27 · F117 — the flyout is the MINI-cart; this is the way through to the full review.
            FOUNDER CORRECTION 2026-09-25 · item 1 — the full review is the in-funnel MY CART page for the
            basket's own mountain, not a standalone /cart screen (lib/cart-entry.ts). Rendered only when
            there is something to review, so an empty flyout does not offer a trip to an empty page. */}
        {items.length > 0 ? (
          <a data-cart-view-cart href={cartReviewHref(items, null)} style={viewCartLink}>
            View cart
          </a>
        ) : null}

        {totalsBlock("bottom")}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// STYLE CONSTANTS — inline + self-contained so they never depend on the mirror CSSOM or leak into the
// static gate. Colours are live's own tokens ($orangeColor #f15a22, $primaryColor #828282,
// $secondaryOpacity #cbe0e4).
//
// The FORM FACTOR is a recorded override of live (F111, RW-15(d) class): live serves a small anchored
// 304×385 panel under the cart button; the founder ruled for a slide-out slider. F123 keeps that ruling
// and changes only the INTERIOR — the axis, the billing block and the tip.
// ---------------------------------------------------------------------------------------------------
const backdrop: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  // live dims nothing — keep a transparent hit-layer so click-outside still closes the tray.
  background: "transparent",
  zIndex: 100000,
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
};
const panel: React.CSSProperties = {
  width: "min(380px, 92vw)",
  height: "100vh",
  background: "#fff",
  boxShadow: "-8px 0 24px rgba(0,0,0,0.18)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "inherit",
  overflowY: "auto",
};
const sectionHeading: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px 8px",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.6px",
  color: "#828282",
  background: "#f7f7f7",
  borderBottom: "1px solid #f2f2f2",
};
const sectionIcon: React.CSSProperties = { display: "inline-flex", width: 18, height: 18, flex: "0 0 18px" };
const editLink: React.CSSProperties = {
  color: "#f15a22",
  textDecoration: "underline",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0,
};
const chevronBtn: React.CSSProperties = {
  border: "none",
  background: "none",
  cursor: "pointer",
  fontSize: 14,
  lineHeight: 1,
  color: "#828282",
  padding: "0 2px",
};
const savingsText: React.CSSProperties = { fontSize: 12, color: "#f15a22", fontWeight: 600 };
const totalBar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 14px",
  borderBottom: "1px solid #cbe0e4",
  fontWeight: 700,
  color: "#222",
};
const vDivider: React.CSSProperties = { flex: 1, borderRight: "1px solid #cbe0e4", alignSelf: "stretch" };
const closeBtn: React.CSSProperties = { border: "none", background: "none", fontSize: 22, cursor: "pointer", lineHeight: 1 };
const list: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };
// S30 · F175 — the mountain sub-heading inside a section, drawn only for a mixed basket. Deliberately
// lighter than the section heading above it so the MARKETPLACE/TRANSPORT split stays the dominant axis
// (that is the axis the billing subtotals reconcile against) and the mountain reads as a grouping within
// it, not as a competing one.
const ORANGE = "#f15a22";
const mountainHeading: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 14px",
  background: "#fbfcfe",
  borderBottom: "1px solid #eef1f5",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".04em",
  textTransform: "uppercase",
  color: "#1f2933",
};
const line: React.CSSProperties = { display: "flex", gap: 12, padding: "12px 14px", borderBottom: "1px solid #f2f2f2", fontSize: 13 };
const promoRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderTop: "1px solid #f2f2f2" };
const promoInput: React.CSSProperties = { flex: 1, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 12, letterSpacing: "0.5px", background: "#f7f7f7", cursor: "not-allowed" };
const billingList: React.CSSProperties = { listStyle: "none", margin: 0, padding: "0 14px 8px" };
const billingRow: React.CSSProperties = { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#828282", padding: "4px 0" };
const tipBox: React.CSSProperties = { padding: "10px 14px", borderTop: "1px solid #f2f2f2" };
const tipInput: React.CSSProperties = { width: 64, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, textAlign: "right" };
const bottomTotal: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderTop: "1px solid #cbe0e4", fontWeight: 700, color: "#222", marginTop: "auto" };
const signinBox: React.CSSProperties = { padding: "16px", borderTop: "1px solid #eee", display: "flex", flexDirection: "column" };
const input: React.CSSProperties = { padding: "10px 12px", border: "1px solid #ccc", borderRadius: 6, marginBottom: 8, fontSize: 14 };
const primaryBtn: React.CSSProperties = { padding: "13px 14px", background: "#f15a22", color: "#fff", border: "none", borderRadius: 6, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { border: "none", background: "none", color: "#b42318", fontSize: 12, cursor: "pointer", padding: 0, marginTop: 4 };

// S27 · F117 — the mini-cart's way through to the full review page.
const viewCartLink: React.CSSProperties = {
  display: "block",
  textAlign: "center",
  padding: "10px 16px",
  margin: "4px 16px 0",
  border: "1px solid #f15a22",
  borderRadius: 999,
  color: "#f15a22",
  fontWeight: 700,
  fontSize: 13,
  textDecoration: "none",
};
